import React from 'react';
import { Container } from 'components/shared';
import { Button } from 'components/ui';
import { Link, useNavigate } from 'react-router-dom';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import MediaElement from 'views/calendar/MediaElement';
import useViralShorts from 'utils/hooks/useViralShorts';
import { BsClock } from 'react-icons/bs';
import { HiSparkles } from 'react-icons/hi';
import { motion } from 'framer-motion';
import { HiFire } from 'react-icons/hi2';

/**
 * CreateViralShortCard Component
 * 
 * Card component that prompts users to create a new viral short
 */
const CreateViralShortCard = () => {
    const navigate = useNavigate();

    return (
        <motion.div
            whileTap={{ scale: 0.98 }}
            className="relative overflow-hidden rounded-2xl cursor-pointer group h-full"
            onClick={() => navigate('/viral-short-creator')}
        >
            <div className="h-full bg-gradient-to-br hover:brightness-110 from-green-500 to-emerald-600 dark:from-green-600 dark:to-emerald-700 flex flex-col items-center justify-center p-6 transition-all group-hover:shadow-2xl group-hover:shadow-green-500/30">
                {/* Animated background elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <motion.div
                        animate={{
                            scale: [1, 1.2, 1],
                            opacity: [0.1, 0.2, 0.1]
                        }}
                        transition={{
                            duration: 3,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                        className="absolute -top-12 -left-12 w-48 h-48 bg-white rounded-full blur-3xl"
                    />
                    <motion.div
                        animate={{
                            scale: [1, 1.3, 1],
                            opacity: [0.1, 0.2, 0.1]
                        }}
                        transition={{
                            duration: 3.5,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: 0.5
                        }}
                        className="absolute -bottom-12 -right-12 w-48 h-48 bg-white rounded-full blur-3xl"
                    />
                </div>

                {/* Content */}
                <div className="relative z-10 flex flex-col items-center text-center">
                    <motion.div
                        animate={{
                            rotate: [0, 10, -10, 0],
                            scale: [1, 1.1, 1]
                        }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                        className="w-16 h-16 bg-white/20 backdrop-blur-xl rounded-2xl flex items-center justify-center mb-4 group-hover:bg-white/30 transition-all"
                    >
                        <HiSparkles className="w-8 h-8 text-white" />
                    </motion.div>
                    <h3 className="text-xl font-bold text-white mb-2">
                        Create Viral Short
                    </h3>
                    <p className="text-white/80 text-sm">
                        Generate engaging short videos
                    </p>
                </div>

                {/* Corner decoration */}
                <div className="absolute top-3 right-3">
                    <div className="w-2 h-2 bg-yellow-300 rounded-full animate-pulse" />
                </div>
            </div>
        </motion.div>
    );
};

/**
 * ViralShortsBody Component
 * 
 * Main body component for displaying viral shorts in a masonry grid
 */
const ViralShortsBody = ({
    columnBreakPoints = { 0: 1, 768: 2, 1100: 3, 1400: 4 },
    showPreview = true,
    handleOnSelect = undefined,
    handleOnUnselect = undefined,
    selectedVideos = undefined,
    isSelectable = undefined,
    simplificated = false,
    showCreateButton = true,
    onClose = undefined,
    inModal = false
}) => {
    const {
        viralShorts,
        loading,
        nextLink,
        pendingVideos,
        fetchViralShorts
    } = useViralShorts();

    return (
        <div className={`w-full h-full ${simplificated ? 'flex flex-col' : 'flex flex-col'} gap-8`}>
            {(!!viralShorts?.length || loading) && 
                <Container className={`relative ${simplificated ? 'flex-1' : 'flex-1 overflow-y-auto'} min-h-full`}>
                    <div className={`relative flex flex-col gap-8 shrink-0 max-w-full ${simplificated ? 'lg:max-w-full' : 'lg:max-w-[250px]'} max-h-[512px] w-full !max-w-none !w-full !max-h-none`}>
                        {loading && !viralShorts?.length
                            ? <ResponsiveMasonry columnsCountBreakPoints={columnBreakPoints} className={`w-full transition-all`}>
                                <Masonry gutter='0.75rem'>
                                    {[1, 2, 3, 4, 5, 6].map(r => (
                                        <div key={r} className="aspect-video bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse"></div>
                                    ))}
                                </Masonry>
                              </ResponsiveMasonry>
                            : <ResponsiveMasonry columnsCountBreakPoints={columnBreakPoints} className={`w-full transition-all`}>
                                <Masonry gutter='1.5rem'>
                                    {/* Create Viral Short Card - First Element */}
                                    {showCreateButton && <CreateViralShortCard />}
                                    
                                    {viralShorts?.filter(result => result.status != 'failed')?.map((result, key) => {
                                        const isPending = pendingVideos.has(result.id) || result.status === 'pending' || result.status === 'processing';
                                        const videoURL = result?.videos?.[0]?.url || result?.response?.split(",")?.[0];
                                        const videoThumbnailURL = result?.videos?.[0]?.thumbnail?.url || videoURL;
                                        
                                        if (isPending) {
                                            return (
                                                <div key={key} className="aspect-video bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 rounded-2xl animate-pulse flex items-center justify-center">
                                                    <div className="text-center">
                                                        <motion.div
                                                            animate={{
                                                                rotate: 360
                                                            }}
                                                            transition={{
                                                                duration: 2,
                                                                repeat: Infinity,
                                                                ease: "linear"
                                                            }}
                                                        >
                                                            <BsClock className="w-6 h-6 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                                                        </motion.div>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Processing...</p>
                                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">This may take a few minutes</p>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        if (!videoURL || result?.status === 'failed') {
                                            return null;
                                        }
                                        
                                        return (
                                            <span 
                                                key={key}
                                                className={`${!!showPreview && `transition-all overflow-hidden`} !rounded-2xl`}
                                            >
                                                <MediaElement 
                                                    key={videoURL}
                                                    src={videoThumbnailURL}
                                                    originalSrc={videoURL}
                                                    handleOnSelect={handleOnSelect}
                                                    handleOnUnselect={handleOnUnselect}
                                                    selected={!!selectedVideos?.find(e => e.originalSrc === videoURL)} 
                                                    isSelectable={isSelectable}
                                                    isEditable={false}
                                                    mediaDescription={`Prompt: "${result?.query}"`}
                                                    aiImageModel={result?.model}
                                                    user={result?.user}
                                                    isVideo={true}
                                                    onError={(e) => {
                                                        try { e.target.parentElement.parentElement.style = "display: none" } catch (err) {}
                                                    }}
                                                />
                                            </span>
                                        );
                                    })}
                                </Masonry>
                              </ResponsiveMasonry>
                        }
                        {!!nextLink && 
                            <div className="w-full flex justify-center mt-6">
                                <Button 
                                    variant="solid"
                                    onClick={() => fetchViralShorts(false)}
                                    loading={loading}
                                    className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/40 transition-all"
                                >
                                    Load More
                                </Button>
                            </div>
                        }
                    </div>
                </Container>
            }

            {/* Empty State */}
            {!loading && !viralShorts?.length && (
                <div className="flex flex-col items-center justify-center py-16 px-4">
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{
                            type: "spring",
                            stiffness: 260,
                            damping: 20
                        }}
                        className="relative mb-6"
                    >
                        <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-2xl shadow-green-500/50">
                            <HiFire className="w-10 h-10 text-white" />
                        </div>
                        {/* Decorative elements */}
                        <motion.div
                            animate={{
                                scale: [1, 1.3, 1],
                                opacity: [0.5, 0.8, 0.5]
                            }}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-300 rounded-full"
                        />
                        <motion.div
                            animate={{
                                scale: [1, 1.2, 1],
                                opacity: [0.3, 0.6, 0.3]
                            }}
                            transition={{
                                duration: 2.5,
                                repeat: Infinity,
                                ease: "easeInOut",
                                delay: 0.5
                            }}
                            className="absolute -bottom-2 -left-2 w-4 h-4 bg-yellow-200 rounded-full"
                        />
                    </motion.div>
                    
                    <motion.h3
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-2xl font-bold text-gray-900 dark:text-white mb-2"
                    >
                        No Viral Shorts Yet
                    </motion.h3>
                    
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="text-gray-600 dark:text-gray-400 text-center max-w-md mb-8"
                    >
                        {!inModal 
                            ? "Start creating engaging short-form videos powered by AI. Perfect for social media platforms like TikTok, Instagram Reels, and YouTube Shorts."
                            : "You haven't generated any viral shorts yet. Generate your first video to see it here."
                        }
                    </motion.p>
                    
                    {!inModal ? (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                        >
                            <Link to="/viral-short-creator">
                                <Button 
                                    variant="solid"
                                    className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/40 transition-all"
                                    icon={<HiSparkles />}
                                >
                                    Create Your First Viral Short
                                </Button>
                            </Link>
                        </motion.div>
                    ) : (
                        onClose && (
                            <Button 
                                variant="solid"
                                onClick={onClose}
                                className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white"
                            >
                                Close
                            </Button>
                        )
                    )}
                </div>
            )}
        </div>
    );
};

export default ViralShortsBody;

